import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus';
import { faMinus } from '@fortawesome/pro-light-svg-icons/faMinus';
import { faDash } from '@fortawesome/pro-light-svg-icons/faDash';
import { TypedMemo } from '../../../common/types';

interface PercentageChangeElementProps {
  percentageChange?: number | null;
  /**
   * When true, inverts the color logic:
   * - Positive changes become red (bad)
   * - Negative changes become green (good)
   * Use this for metrics where decrease is desirable (e.g., "Playlist Loss Score")
   */
  invertColorLogic?: boolean;
}

const PercentageChangeElementComponent: React.FC<PercentageChangeElementProps> = ({
  percentageChange,
  invertColorLogic = false,
}) => {
  const getPercentageInfo = () => {
    if (percentageChange === null || percentageChange === undefined) {
      return {
        icon: faDash,
        className: 'change-neutral',
        change: 'N/A',
      };
    }

    if (percentageChange > 0) {
      return {
        icon: faPlus,
        // When inverted, positive change is bad (e.g., more losses)
        className: invertColorLogic ? 'change-negative' : 'change-positive',
        change: `${Math.round(percentageChange)}%`,
      };
    }

    if (percentageChange < 0) {
      return {
        icon: faMinus,
        // When inverted, negative change is good (e.g., fewer losses)
        className: invertColorLogic ? 'change-positive' : 'change-negative',
        change: `${Math.round(Math.abs(percentageChange))}%`,
      };
    }

    return {
      icon: faDash,
      className: 'change-neutral',
      change: '0%',
    };
  };

  const percentageInfo = getPercentageInfo();

  return (
    <div
      className={`${percentageInfo.className} d-flex align-items-center justify-content-center rounded px-2 py-1 gap-1 font-monospace`}
      style={{ minWidth: 36 }}
    >
      <FontAwesomeIcon icon={percentageInfo.icon} size="sm" />
      {percentageInfo.change !== 'N/A' && <span>{percentageInfo.change}</span>}
    </div>
  );
};

export const PercentageChangeElement = TypedMemo(PercentageChangeElementComponent);
